import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useState } from "react";
import { Field, Form, Formik, FormikHelpers } from "formik";
import axios from "axios";
import {
  Dropdown,
  FormButton,
  PhoneField,
  PrivacyPolicyBlock,
} from "./RegisterStyles";
import { NavLink, useNavigate } from "react-router-dom";
import { phoneRegExp } from "../../../constants/Regs";
import { Loader } from "../../../components/Loader/Loader";
import {
  ArticleBlock,
  FormMainBlock,
  ShowPasswordBlock,
  TheLine,
} from "../FormsGeneralStyles";
import { RegisterFormValues } from "../../../interfaces/RegisterInterface";
import { useAppSelector } from "../../../store/hooks";

export const Register = () => {
  const [selectedCode, setSelectedCode] = useState("+33");
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [isSuccess, setSuccess] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const lang = useAppSelector((state) => state.locale.localStorageValue);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const phoneCodes = [
    { code: "+33", label: t("countries.france") },
    { code: "+7", label: t("countries.russia") },
    { code: "+49", label: t("countries.germany") },
    { code: "+1", label: t("countries.usa") },
    { code: "+44", label: t("countries.uk") },
    { code: "another", label: t("countries.anotherCountry") },
  ];

  const togglePasswordVisibility = (fieldId: string) => {
    if (fieldId === "password") {
      setShowPassword((prev) => !prev);
    } else {
      setShowRepeatPassword((prev) => !prev);
    }
  };

  const DisplayingErrorMessagesSchema = Yup.object().shape({
    first_name: Yup.string()
      .min(2, t("formsWarnings.tooShort"))
      .max(50, t("formsWarnings.tooLong"))
      .required(t("formsWarnings.symbols")),
    last_name: Yup.string()
      .min(2, t("formsWarnings.tooShort"))
      .max(50, t("formsWarnings.tooLong"))
      .required(t("formsWarnings.symbols")),
    email: Yup.string()
      .email(t("formsWarnings.invalidEmail"))
      .required(t("formsWarnings.emailRequired")),
    phone: Yup.string()
      .matches(phoneRegExp, t("formsWarnings.phoneNumber"))
      .required(t("formsWarnings.enterPhoneNumber")),
    password: Yup.string().required(t("formsWarnings.passwordRequired")),
    password_confirmation: Yup.string()
      .oneOf([Yup.ref("password"), ""], t("formsWarnings.passwordMismatch"))
      .required(t("formsWarnings.repeatPasswordRequired")),
    isBoxChecked: Yup.boolean().oneOf(
      [true],
      t("formsWarnings.fieldMustBeMarked")
    ),
  });

  const handleSubmit = async (
    values: RegisterFormValues,
    formikHelpers: FormikHelpers<RegisterFormValues>
  ) => {
    setInProgress(true);
    setSuccess(false);

    const fullPhoneNumber =
      selectedCode === "another"
        ? values.phone
        : `${selectedCode}${values.phone}`;
    console.log("Отправляем номер:", fullPhoneNumber);

    try {
      const response = await axios.post(
        "https://api.demo-q17.com/auth/register",
        {
          email: values.email,
          first_name: values.first_name,
          last_name: values.last_name,
          phone: fullPhoneNumber,
          password: values.password,
          password_confirmation: values.password_confirmation,
        },
        {
          headers: {
            "Accept-Language": lang,
            "Accept": "application/json",
          },
        }
      );

      setSuccess(true);

      if (lang === "en") {
        navigate("/auth/register/confirmed");
      } else {
        navigate(`/${lang}/auth/register/confirmed`);
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const { status, data } = error.response;

        if (status === 422 && data.errors) {
          Object.keys(data.errors).forEach((field) => {
            formikHelpers.setFieldError(field, data.errors[field]);
          });
        } else {
          console.error("Unhandled server error:", error.response.data);
        }
      } else {
        console.error("Unexpected error:", error);
      }
    } finally {
      setInProgress(false);
    }
  };

  return (
    <main>
      <FormMainBlock>
        <section>
          <div className="container__standart div__container">
            <div className="div__form">
              <h3>{t("registerPage.title1")}</h3>
              <Formik
                initialValues={{
                  first_name: "",
                  last_name: "",
                  email: "",
                  phone: "",
                  password: "",
                  password_confirmation: "",
                  isBoxChecked: false,
                }}
                validationSchema={DisplayingErrorMessagesSchema}
                onSubmit={handleSubmit}
              >
                {({ errors, touched }) => (
                  <Form className="form__content">
                    <ArticleBlock>
                      <div className="div__main__block">
                        <Field
                          id="first_name"
                          name="first_name"
                          placeholder={t("form.name")}
                          type="text"
                          className={`input__styles ${
                            touched.first_name &&
                            errors.first_name &&
                            "!border-[#eb5757]"
                          }`}
                        />
                        {touched.first_name && errors.first_name && (
                          <span className="form__req__info">
                            {errors.first_name}
                          </span>
                        )}
                      </div>
                    </ArticleBlock>
                    <ArticleBlock>
                      <div className="div__main__block">
                        <Field
                          id="last_name"
                          name="last_name"
                          placeholder={t("form.surName")}
                          type="text"
                          className={`input__styles ${
                            touched.last_name &&
                            errors.last_name &&
                            "!border-[#eb5757]"
                          }`}
                        />
                        {touched.last_name && errors.last_name && (
                          <span className="form__req__info">
                            {errors.last_name}
                          </span>
                        )}
                      </div>
                    </ArticleBlock>
                    <ArticleBlock>
                      <PhoneField>
                        <Dropdown
                          value={selectedCode}
                          onChange={(e) => setSelectedCode(e.target.value)}
                        >
                          {phoneCodes.map((item) => (
                            <option key={item.code} value={item.code}>
                              {item.label} (
                              {item.code !== "another" ? item.code : ""})
                            </option>
                          ))}
                        </Dropdown>
                        <Field
                          id="phone"
                          name="phone"
                          placeholder={
                            selectedCode === "another"
                              ? t("form.fullPhone")
                              : t("form.phone")
                          }
                          type="text"
                          className={`input__styles ${
                            touched.phone && errors.phone && "!border-[#eb5757]"
                          }`}
                        />
                      </PhoneField>
                      {touched.phone && errors.phone && (
                        <span className="form__req__info">{errors.phone}</span>
                      )}
                    </ArticleBlock>
                    <ArticleBlock>
                      <div className="div__main__block">
                        <Field
                          id="email"
                          name="email"
                          placeholder={t("form.mail")}
                          type="text"
                          className={`input__styles ${
                            touched.email && errors.email && "!border-[#eb5757]"
                          }`}
                        />
                        {touched.email && errors.email && (
                          <span className="form__req__info">
                            {errors.email}
                          </span>
                        )}
                      </div>
                    </ArticleBlock>
                    <ArticleBlock>
                      <div className="div__main__block">
                        <Field
                          id="password"
                          name="password"
                          placeholder={t("form.password")}
                          type={showPassword ? "text" : "password"}
                          className={`input__styles ${
                            touched.password &&
                            errors.password &&
                            "!border-[#eb5757]"
                          }`}
                        />
                        <ShowPasswordBlock
                          onClick={() => {
                            togglePasswordVisibility("password");
                          }}
                        >
                          <span></span>
                        </ShowPasswordBlock>
                        {touched.password && errors.password && (
                          <span className="form__req__info password__error">
                            {errors.password}
                          </span>
                        )}
                      </div>
                    </ArticleBlock>
                    <ArticleBlock>
                      <div className="div__main__block password__error">
                        <Field
                          id="password_confirmation"
                          name="password_confirmation"
                          placeholder={t("form.repeatPassword")}
                          type={showRepeatPassword ? "text" : "password"}
                          className={`input__styles ${
                            touched.password &&
                            errors.password &&
                            "!border-[#eb5757]"
                          }`}
                        />
                        <ShowPasswordBlock
                          onClick={() => {
                            togglePasswordVisibility("password_confirmation");
                          }}
                        >
                          <span></span>
                        </ShowPasswordBlock>
                        {touched.password && errors.password && (
                          <span className="form__req__info password__error">
                            {errors.password}
                          </span>
                        )}
                      </div>
                    </ArticleBlock>
                    <TheLine>
                      <span></span>
                    </TheLine>
                    <ArticleBlock>
                      <PrivacyPolicyBlock className="div__main__block">
                        <label className="label__block">
                          <Field
                            type="checkbox"
                            name="isBoxChecked"
                            className="checkbox__style"
                          />
                          <span>
                            {t("registerPage.blockInfo11")}
                            <NavLink
                              to="/"
                              target="_blank"
                              className="privacy__link"
                            >
                              {t("registerPage.blockInfo12")}
                            </NavLink>
                          </span>
                        </label>
                        {touched.isBoxChecked && errors.isBoxChecked && (
                          <span className="form__req__info">
                            {errors.isBoxChecked}
                          </span>
                        )}
                      </PrivacyPolicyBlock>
                    </ArticleBlock>
                    <div>
                      {inProgress ? (
                        <Loader height="41" width="100%" />
                      ) : (
                        <FormButton
                          type="submit"
                          className="white__button flex__center log__btn"
                        >
                          {isSuccess
                            ? t("form.successfully")
                            : t("form.register")}
                        </FormButton>
                      )}
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </section>
      </FormMainBlock>
    </main>
  );
};
